import type { LocalizedText } from "./site-data";

// web/src/showcase-data.ts
// 「展示合集」页的**唯一数据源**。要增删视频，只改这个文件即可，
// 页面结构与中英文案由 src/showcase.ts 渲染。
//
// - 这里放的是**视频链接**，页面只做外链跳转，不内嵌播放器。
// - 标题：中文用 B 站原标题；英文优先用 YouTube 上那条视频的英文标题。
//   `url` = B 站（中文版主链接），`urlEn` = YouTube（英文版主链接；中文版会额外显示一个入口）。
// - 本文件由 `scripts/build-showcase-data.mjs` 首先生成（数据来自作者手动保存的
//   空间投稿页 HTML，见 `我的B站视频html/`），再由 `scripts/add-youtube-to-showcase.mjs`
//   补上 YouTube 侧信息（`documentation/_bv_to_youtube.json`）；
//   生成之后**请直接编辑本文件**，不要重复运行生成脚本，以免覆盖手工调整。
// - 视频总量 320 条，这里收的是教学/演示/功能类全部 + 每款乐器的代表作；
//   与 MusicDoll 无关的早期内容（2021–2024 杂项）未收录。
// - 英文版只渲染有 `urlEn` 的条目（B 站内容在英文站一律不出现）。

function l(zh: string, en: string): LocalizedText {
  return { zh, en };
}

export interface ShowcaseItem {
  /** 视频标题：中文用 B 站原标题，英文优先用 YouTube 标题 */
  title: LocalizedText;
  /** 一句话说明这条视频能解决什么问题（可选） */
  note?: LocalizedText;
  /** B 站视频页链接（中文版主链接；英文版不显示） */
  url?: string;
  /** YouTube 视频页链接（英文版主链接；中文版作为附加入口） */
  urlEn?: string;
  /** 标签 */
  tags?: LocalizedText[];
}

export interface ShowcaseGroup {
  /** 锚点 id，同时用于页内大纲 */
  id: string;
  title: LocalizedText;
  desc?: LocalizedText;
  items: ShowcaseItem[];
}

export const SHOWCASE_GROUPS: ShowcaseGroup[] = [
  {
    id: "intro-demos",
    title: l("产品介绍与使用演示", "Product intros and usage demos"),
    desc: l("每款乐器的「是什么 + 怎么用」入门视频，先看这些建立整体印象。", "What each instrument does and how to use it — start with these."),
    items: [
      {
        title: l("\"并夕夕版林离？\" 钢琴动画生成软件 KeyRipple 简介", "Master 3D Piano Performance Animation with KeyRipple | Complete Blender & Unreal Engine Guide"),
        url: "https://www.bilibili.com/video/BV1icG36sEuB/",
        urlEn: "https://www.youtube.com/watch?v=GMIgjdpK4SQ",
        note: l("KeyRipple 的软件简介：界面构成与能做什么。", "KeyRipple introduction: what it does and how the UI is laid out."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("钢琴动画软件KeyRipple 开放免费下载及使用 让你的动画人物弹钢琴", "钢琴动画软件KeyRipple 开放免费下载及使用 让你的动画人物弹钢琴"),
        url: "https://www.bilibili.com/video/BV1ms7A6eEo7/",
        note: l("免费入口的下载与上手说明。", "Download and first-run walkthrough for the free entry point."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("1.keyRipple软件介绍", "From Midi to Animation: KeyRipple Introduce"),
        url: "https://www.bilibili.com/video/BV1A4SqBPEAY/",
        urlEn: "https://www.youtube.com/watch?v=M0-WVsCM66k",
        note: l("KeyRipple：软件介绍。", "KeyRipple: software introduction."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("2.keyRipple使用演示视频", "How to use KeyRipple"),
        url: "https://www.bilibili.com/video/BV1HhSqBhEbw/",
        urlEn: "https://www.youtube.com/watch?v=Q4gSBsoMV5k",
        note: l("KeyRipple：使用演示。", "KeyRipple: usage demo."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("1.stringFlow介绍视频", "1.stringFlow介绍视频"),
        url: "https://www.bilibili.com/video/BV1jBSqBdEmc/",
        note: l("StringFlow：介绍视频。", "StringFlow: introduction."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("2.stringFlow使用演示", "2.stringFlow使用演示"),
        url: "https://www.bilibili.com/video/BV15CSqBpEXp/",
        note: l("StringFlow：使用演示。", "StringFlow: usage demo."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("1.beatBloom介绍视频", "1.beatBloom介绍视频"),
        url: "https://www.bilibili.com/video/BV1KnSqBMEHV/",
        note: l("BeatBloom：介绍视频。", "BeatBloom: introduction."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("2.beatBloom使用演示", "2.beatBloom使用演示"),
        url: "https://www.bilibili.com/video/BV1WsSqBvEzM/",
        note: l("BeatBloom：使用演示。", "BeatBloom: usage demo."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("WindRise篇：吹奏类乐器动画介绍", "Windrise: Introduction"),
        url: "https://www.bilibili.com/video/BV1Hkbn6KE5e/",
        urlEn: "https://www.youtube.com/watch?v=-DiYTEWQXIA",
        note: l("WindRise：吹奏类乐器动画介绍。", "WindRise: wind-instrument animation overview."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("WindRise篇：吹奏类乐器动画原理", "From MIDI to Performance: Wind Instrument Principles Explained"),
        url: "https://www.bilibili.com/video/BV1Hkbn6KEA2/",
        urlEn: "https://www.youtube.com/watch?v=8GLhnyequfU",
        note: l("WindRise：吹奏类乐器动画原理。", "WindRise: the principles behind wind-instrument animation."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("如何在虚幻引擎中弹古筝 第0集 简介", "How To Playing GuZheng In Unreal Episode 0: Introduction"),
        url: "https://www.bilibili.com/video/BV1GFTQ6SE7E/",
        urlEn: "https://www.youtube.com/watch?v=1PF-218E4KU",
        note: l("ZhengDrift：古筝篇第 0 集，先看简介。", "ZhengDrift: episode 0 of the guzheng series."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("FretDance使用介绍 blender & python & 吉它 & animation", "FretDance使用介绍 blender & python & 吉它 & animation"),
        url: "https://www.bilibili.com/video/BV1UT421i71j/",
        note: l("FretDance 使用介绍（Blender + Python + 吉他）。", "FretDance introduction (Blender + Python + guitar)."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("Midi转演奏动画工具 - FretDance桌面版开放下载", "Midi转演奏动画工具 - FretDance桌面版开放下载"),
        url: "https://www.bilibili.com/video/BV1ASH7zbEr5/",
        note: l("FretDance 桌面版开放下载。", "FretDance desktop edition released."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("桌面版的使用讲解", "桌面版的使用讲解"),
        url: "https://www.bilibili.com/video/BV1ASH7zbEtD/",
        note: l("FretDance 桌面版的使用讲解。", "FretDance desktop edition walkthrough."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("HarpGlide主流程跑通！芭芭拉冲呀！", "First HarpGlide Video - Barbara Play Vourukasha Oasis BGM"),
        url: "https://www.bilibili.com/video/BV13hJH6cEAX/",
        urlEn: "https://www.youtube.com/watch?v=lG-akWZm3fc",
        note: l("HarpGlide 主流程跑通演示。", "HarpGlide main pipeline working end to end."),
        tags: [l("总览", "Overview")],
      },
      {
        title: l("动画师请看过来！全新动画领域正在招募！", "动画师请看过来！全新动画领域正在招募！"),
        url: "https://www.bilibili.com/video/BV1Y5B5B9EjQ/",
        note: l("面向动画师的整体介绍。", "Overall introduction aimed at animators."),
        tags: [l("总览", "Overview")],
      },
    ],
  },
  {
    id: "quickstart",
    title: l("快速上手（Blender / Unreal）", "Quick start (Blender / Unreal)"),
    desc: l("从插件安装到跑出第一段动画的最短路径，双端各一条。", "The shortest path from installing the plugin to your first animation, one per platform."),
    items: [
      {
        title: l("快速上手 | Blender", "Quick Start: Blender"),
        url: "https://www.bilibili.com/video/BV1ofGg6dESA/",
        urlEn: "https://www.youtube.com/watch?v=OqLbeqA5Ysc",
        note: l("Blender 侧的完整快速上手。", "Full quick start on the Blender side."),
        tags: [l("快速上手", "Quick Start")],
      },
      {
        title: l("快速上手 | Unreal", "Quick Start: Unreal Engine"),
        url: "https://www.bilibili.com/video/BV1rDGg6wES1/",
        urlEn: "https://www.youtube.com/watch?v=Gbxf71i6__Q",
        note: l("Unreal 侧的完整快速上手。", "Full quick start on the Unreal side."),
        tags: [l("快速上手", "Quick Start")],
      },
    ],
  },
  {
    id: "custom-performer-dual",
    title: l("自定义人物完整流程（Blender / Unreal 对照）", "Custom performer pipeline (Blender / Unreal side by side)"),
    desc: l("同一件事在两个平台分别怎么做，建议一前一后对着看。", "The same task on both platforms — watch them side by side."),
    items: [
      {
        title: l("自定义人物流程介绍 | 双端通用", "Custom Character: Core Concepts"),
        url: "https://www.bilibili.com/video/BV1zRGg6bEK1/",
        urlEn: "https://www.youtube.com/watch?v=OFEhLPQ8Ctc",
        tags: [l("自定义人物", "Custom performer")],
      },
      {
        title: l("人物骨骼预处理 | 双端通用", "Character Skeleton Pre-processing"),
        url: "https://www.bilibili.com/video/BV1z9Gg6FEyL/",
        urlEn: "https://www.youtube.com/watch?v=hiUk10q4taQ",
        tags: [l("自定义人物", "Custom performer")],
      },
      {
        title: l("初始化KeyRipple | Blender", "Initializing KeyRipple (Blender)"),
        url: "https://www.bilibili.com/video/BV1agG36EE4k/",
        urlEn: "https://www.youtube.com/watch?v=A8wC8CtTecw",
        tags: [l("自定义人物", "Custom performer")],
      },
      {
        title: l("初始化KeyRipple | Unreal", "Initializing KeyRipple (Unreal)"),
        url: "https://www.bilibili.com/video/BV1XMG36SETG/",
        urlEn: "https://www.youtube.com/watch?v=FV5_zXbwKxQ",
        tags: [l("自定义人物", "Custom performer")],
      },
      {
        title: l("设置控制链| Blender", "Setting Up Control Chains (Blender)"),
        url: "https://www.bilibili.com/video/BV1DVG36jETc/",
        urlEn: "https://www.youtube.com/watch?v=p0yK5Asm8P4",
        tags: [l("自定义人物", "Custom performer")],
      },
      {
        title: l("设置控制链 | Unreal", "Setting Up Control Chains (Unreal)"),
        url: "https://www.bilibili.com/video/BV1ePG36pEZo/",
        urlEn: "https://www.youtube.com/watch?v=5Hp2FajLQXs",
        tags: [l("自定义人物", "Custom performer")],
      },
      {
        title: l("设置基准姿势 | Blender", "Setting Up Reference Poses (Blender)"),
        url: "https://www.bilibili.com/video/BV1vwG369EYs/",
        urlEn: "https://www.youtube.com/watch?v=dObMANaWiNs",
        tags: [l("自定义人物", "Custom performer")],
      },
      {
        title: l("设置基准姿势 | Unreal", "Setting Up Reference Poses (Unreal)"),
        url: "https://www.bilibili.com/video/BV1YTG36nEay/",
        urlEn: "https://www.youtube.com/watch?v=AJVzUYT_9Yk",
        tags: [l("自定义人物", "Custom performer")],
      },
      {
        title: l("收尾工作 | Blender", "Finishing Touches (Blender)"),
        url: "https://www.bilibili.com/video/BV15NG36XEpk/",
        urlEn: "https://www.youtube.com/watch?v=klrNsbTTiNQ",
        tags: [l("自定义人物", "Custom performer")],
      },
      {
        title: l("收尾工作 | Unreal", "Finishing Touches (Unreal)"),
        url: "https://www.bilibili.com/video/BV18YGg6yEAw/",
        urlEn: "https://www.youtube.com/watch?v=UXWGycK9kBU",
        tags: [l("自定义人物", "Custom performer")],
      },
      {
        title: l("验收成果 | Blender", "Verifying Results (Blender)"),
        url: "https://www.bilibili.com/video/BV1miGg6rECi/",
        urlEn: "https://www.youtube.com/watch?v=K95yZjY1MP8",
        tags: [l("自定义人物", "Custom performer")],
      },
      {
        title: l("验收成果 | Unreal", "Verifying Results (Unreal)"),
        url: "https://www.bilibili.com/video/BV1bvGg6VEQX/",
        urlEn: "https://www.youtube.com/watch?v=ERbeJxkHrgA",
        tags: [l("自定义人物", "Custom performer")],
      },
      {
        title: l("相关视频推荐 | 通用", "相关视频推荐 | 通用"),
        url: "https://www.bilibili.com/video/BV1X4Gg6mEFC/",
        tags: [l("自定义人物", "Custom performer")],
      },
    ],
  },
  {
    id: "series-musicdoll",
    title: l("MusicDoll 系列教程（第 0–13 集）", "MusicDoll series tutorial (episodes 0–13)"),
    desc: l("以大提琴为例，从骨骼预处理一路讲到 Unreal 里跑通，最完整的一套流程讲解。", "The most complete pipeline walkthrough, using the cello as the example, from rig prep to a working Unreal setup."),
    items: [
      {
        title: l("第0集 前言", "0. Introduce"),
        url: "https://www.bilibili.com/video/BV1g1tP6KEfN/",
        urlEn: "https://www.youtube.com/watch?v=jdWzO_DZdeU",
        tags: [l("系列教程", "Series")],
      },
      {
        title: l("第1集 人物和乐器模型导入Blender", "1.Importing the Character and Instrument Models to Blender"),
        url: "https://www.bilibili.com/video/BV1eqtP6rEzt/",
        urlEn: "https://www.youtube.com/watch?v=HFyOOBpCin0",
        tags: [l("系列教程", "Series")],
      },
      {
        title: l("第2集 在Blender中初始化控制体系", "2.Initializing the Control System"),
        url: "https://www.bilibili.com/video/BV1LitP6GEyf/",
        urlEn: "https://www.youtube.com/watch?v=icJNX5eSryQ",
        tags: [l("系列教程", "Series")],
      },
      {
        title: l("第3集 Controller Root和乐器绑定", "3. Controller Root and Instrument Binding"),
        url: "https://www.bilibili.com/video/BV1vitP6GEsY/",
        urlEn: "https://www.youtube.com/watch?v=sEFyosy6u64",
        tags: [l("系列教程", "Series")],
      },
      {
        title: l("第4集 骨骼控制原理A", "4. How Bone Control Works - Part A"),
        url: "https://www.bilibili.com/video/BV1VztP6gEv7/",
        urlEn: "https://www.youtube.com/watch?v=ZCYYkrQt3UA",
        tags: [l("系列教程", "Series")],
      },
      {
        title: l("第5集 骨骼控制原理B", "5. How Bone Control Works - Part B"),
        url: "https://www.bilibili.com/video/BV1NdtP6bEeT/",
        urlEn: "https://www.youtube.com/watch?v=WxgkwA6D-Bw",
        tags: [l("系列教程", "Series")],
      },
      {
        title: l("第6集 大提琴几何数据采集及琴弦制作", "6. Capturing the Instrument's Geometry Data"),
        url: "https://www.bilibili.com/video/BV12RtP6FEJe/",
        urlEn: "https://www.youtube.com/watch?v=qsPL3JEwm00",
        tags: [l("系列教程", "Series")],
      },
      {
        title: l("第7集 设置大提琴基准姿势", "7. Setting the Reference Poses"),
        url: "https://www.bilibili.com/video/BV14RtP6FETJ/",
        urlEn: "https://www.youtube.com/watch?v=qeOQON5k-dM",
        tags: [l("系列教程", "Series")],
      },
      {
        title: l("第8集 在Blender中测试与debug林尼", "8. Generate Animation And Debug In Blender"),
        url: "https://www.bilibili.com/video/BV1PftP64EKV/",
        urlEn: "https://www.youtube.com/watch?v=U3_ahzPnd-0",
        tags: [l("系列教程", "Series")],
      },
      {
        title: l("第9集 从Blender中导出Music Doll", "9. Exporting Music Doll From Blender"),
        url: "https://www.bilibili.com/video/BV1NotP6ZEFp/",
        urlEn: "https://www.youtube.com/watch?v=OKS-cokBjh4",
        tags: [l("系列教程", "Series")],
      },
      {
        title: l("第10集 在Unreal中初始化MusicDoll", "10. Initializing Music Doll In Unreal Engine"),
        url: "https://www.bilibili.com/video/BV1MStP6YEhA/",
        urlEn: "https://www.youtube.com/watch?v=NlPBghjbFRs",
        tags: [l("系列教程", "Series")],
      },
      {
        title: l("第11集 完善Unreal中的控制链", "11. Setting Up the Control Chain on the Unreal Side"),
        url: "https://www.bilibili.com/video/BV1uUtP6PEnq/",
        urlEn: "https://www.youtube.com/watch?v=9Brk-HVLkg4",
        tags: [l("系列教程", "Series")],
      },
      {
        title: l("第12集 在Unreal中测试林尼", "12. Generating Animation and Debug In UE"),
        url: "https://www.bilibili.com/video/BV1TXtP6hEG1/",
        urlEn: "https://www.youtube.com/watch?v=Vie_f65H6a0",
        tags: [l("系列教程", "Series")],
      },
      {
        title: l("第13集 结语", "第13集 结语"),
        url: "https://www.bilibili.com/video/BV1TXtP6hEQB/",
        tags: [l("系列教程", "Series")],
      },
    ],
  },
  {
    id: "series-key-ripple",
    title: l("KeyRipple 自定义钢琴演奏者", "KeyRipple — custom piano performer"),
    desc: l("钢琴键制作、双手基准状态、关键位置记录器与琴键发光效果。", "Building the keyboard, both hands' baseline states, key-position recorders and key glow."),
    items: [
      {
        title: l("3.钢琴的导入和钢琴键的制作", "Make Piano Keys"),
        url: "https://www.bilibili.com/video/BV1c8SqBwEBR/",
        urlEn: "https://www.youtube.com/watch?v=lCF3I5uhKrs",
        tags: [l("KeyRipple", "KeyRipple")],
      },
      {
        title: l("4.人物朝向控件的设置", "4.人物朝向控件的设置"),
        url: "https://www.bilibili.com/video/BV1c8SqBwEQV/",
        tags: [l("KeyRipple", "KeyRipple")],
      },
      {
        title: l("5.双手基准状态的设置", "5.双手基准状态的设置"),
        url: "https://www.bilibili.com/video/BV1NHSqBqEvn/",
        tags: [l("KeyRipple", "KeyRipple")],
      },
      {
        title: l("6.关键位置记录器", "6.关键位置记录器"),
        url: "https://www.bilibili.com/video/BV1KnSqBMECG/",
        tags: [l("KeyRipple", "KeyRipple")],
      },
      {
        title: l("7.为钢琴键添加发光效果", "7.为钢琴键添加发光效果"),
        url: "https://www.bilibili.com/video/BV1KnSqBMESM/",
        tags: [l("KeyRipple", "KeyRipple")],
      },
    ],
  },
  {
    id: "series-string-flow",
    title: l("StringFlow「弦上流光」系列", "StringFlow — bowed strings series"),
    desc: l("琴弦制作、Control Rig 初始化、左手位置、人琴弓同步到数据导出。", "String building, Control Rig setup, left-hand placement, bow synchronisation and data export."),
    items: [
      {
        title: l("StringFlowUnreal 弦上流光 系列教程 - 0.前言", "StringFlowUnreal 弦上流光 系列教程 - 0.前言"),
        url: "https://www.bilibili.com/video/BV1MuwezcEQM/",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("1.模型的准备", "1 模型的准备"),
        url: "https://www.bilibili.com/video/BV1HrAjzgEez/",
        urlEn: "https://www.youtube.com/watch?v=y8gjeWKDHXE",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("2.制作琴弦", "2 琴弦的制作"),
        url: "https://www.bilibili.com/video/BV1pJAKzyEPd/",
        urlEn: "https://www.youtube.com/watch?v=kbGB2rLDwJU",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("3.初始化ControlRig", "3 导入人物及初始化"),
        url: "https://www.bilibili.com/video/BV1CsXcBzE4a/",
        urlEn: "https://www.youtube.com/watch?v=kzOe7lnPKek",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("4. 人物控制", "4 人物控制"),
        url: "https://www.bilibili.com/video/BV1sGX7BXEyd/",
        urlEn: "https://www.youtube.com/watch?v=8F9UjCUgLMI",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("5. 左手姿势及小提琴上的位置点", "5  左手姿势及小提琴上的位置点"),
        url: "https://www.bilibili.com/video/BV1BRX3BfEt9/",
        urlEn: "https://www.youtube.com/watch?v=9JV31x98MLw",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("6.人琴弓同步与右手演奏1弦的姿势", "6 人琴弓同步与右手演奏第1弦的姿势"),
        url: "https://www.bilibili.com/video/BV1DaXoBGE2T/",
        urlEn: "https://www.youtube.com/watch?v=LZDzkfg1tmE",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("7.设置结果展示和数据导出", "7 设置结果展示和数据导出"),
        url: "https://www.bilibili.com/video/BV1SJ93B2E4Q/",
        urlEn: "https://www.youtube.com/watch?v=k-piI4jky4k",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("8.StringFlow使用演示和一些设置细节", "8 StringFlow使用演示和一些设置细节"),
        url: "https://www.bilibili.com/video/BV1CE9ABLExf/",
        urlEn: "https://www.youtube.com/watch?v=Bgu3Gi0UgRA",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("9.真实音源生成动画流程介绍", "9 真实音源生成动画 流程介绍 Introduction to the Process of Generating Animations from Real Audio Sources"),
        url: "https://www.bilibili.com/video/BV1MfD3BgE2w/",
        urlEn: "https://www.youtube.com/watch?v=bVOA3DhoBrM",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("3.提琴绑定", "3.提琴绑定"),
        url: "https://www.bilibili.com/video/BV15CSqBpES9/",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("4.提琴基准记录器", "4.提琴基准记录器"),
        url: "https://www.bilibili.com/video/BV1LrSqB9E4B/",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("5.左手基准状态", "5.左手基准状态"),
        url: "https://www.bilibili.com/video/BV1LrSqB9Eku/",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("6.右手基准状态", "6.右手基准状态"),
        url: "https://www.bilibili.com/video/BV1JYSqBFE12/",
        tags: [l("StringFlow", "StringFlow")],
      },
      {
        title: l("7.琴弦动画", "7.琴弦动画"),
        url: "https://www.bilibili.com/video/BV1cYSqBFEP5/",
        tags: [l("StringFlow", "StringFlow")],
      },
    ],
  },
  {
    id: "series-zheng-drift",
    title: l("ZhengDrift「在虚幻引擎中弹古筝」", "ZhengDrift — guzheng in Unreal"),
    desc: l("从人物骨骼、古筝模型到 Control Rig、基准姿势与生成动画。", "From the performer rig and guzheng model to Control Rig, baseline poses and animation generation."),
    items: [
      {
        title: l("如何在虚幻引擎中弹古筝 第1集 人物骨骼", "How To Playing GuZheng In Unreal Episode 1: Armatrue"),
        url: "https://www.bilibili.com/video/BV1VfTD6nEA9/",
        urlEn: "https://www.youtube.com/watch?v=AwZImK3vAdg",
        tags: [l("ZhengDrift", "ZhengDrift")],
      },
      {
        title: l("如何在虚幻引擎中弹古筝第2集 - 古筝模型", "How To Playing GuZheng In Unreal Episode 2: GuZheng"),
        url: "https://www.bilibili.com/video/BV1J9M76zEmJ/",
        urlEn: "https://www.youtube.com/watch?v=8sBFuw1ckM8",
        tags: [l("ZhengDrift", "ZhengDrift")],
      },
      {
        title: l("如何在虚幻引擎中弹古筝 第3集 导入UE", "How To Playing GuZheng In Unreal Episode 3: Import to UE"),
        url: "https://www.bilibili.com/video/BV1LfNM6YERE/",
        urlEn: "https://www.youtube.com/watch?v=-1PE2zwSsig",
        tags: [l("ZhengDrift", "ZhengDrift")],
      },
      {
        title: l("如何在虚幻引擎中弹古筝 第4集 设置Control Rig", "How To Playing GuZheng In Unreal Episode 4: Setting Control Rig"),
        url: "https://www.bilibili.com/video/BV1hhKj6mE4k/",
        urlEn: "https://www.youtube.com/watch?v=NdRCk0t0o-0",
        tags: [l("ZhengDrift", "ZhengDrift")],
      },
      {
        title: l("如何在虚幻引擎中弹古筝 第5集 设置基准姿势", "How To Playing GuZheng In Unreal Episode 5: Reference Pose"),
        url: "https://www.bilibili.com/video/BV13jKh6sEgA/",
        urlEn: "https://www.youtube.com/watch?v=kajX7xDtr1Y",
        tags: [l("ZhengDrift", "ZhengDrift")],
      },
      {
        title: l("如何在虚幻引擎中弹古筝 第6集 生成动画", "How To Playing GuZheng In Unreal Episode 6: Generate Animation"),
        url: "https://www.bilibili.com/video/BV1FwgZ6WE3p/",
        urlEn: "https://www.youtube.com/watch?v=HK1lzEViWcU",
        tags: [l("ZhengDrift", "ZhengDrift")],
      },
    ],
  },
  {
    id: "series-beat-bloom",
    title: l("BeatBloom 鼓手系列", "BeatBloom — drummer series"),
    desc: l("鼓组信息、Shape Key、Control Rig、基准姿势到动作编辑。", "Drum-kit data, shape keys, Control Rig, baseline poses and motion editing."),
    items: [
      {
        title: l("虚幻引擎中的鼓手 第0集 简介", "Drummer In Unreal Engine: Part 0 Introduce"),
        url: "https://www.bilibili.com/video/BV1XDdgBWEVy/",
        urlEn: "https://www.youtube.com/watch?v=6al2rxCNWCk",
        tags: [l("BeatBloom", "BeatBloom")],
      },
      {
        title: l("虚幻引擎中的鼓手 第1集 鼓组信息", "Drummer In Unreal Engine Part 1: Drum Kit Info"),
        url: "https://www.bilibili.com/video/BV1nYRfBTEmx/",
        urlEn: "https://www.youtube.com/watch?v=Y2zJTotbNRc",
        tags: [l("BeatBloom", "BeatBloom")],
      },
      {
        title: l("虚幻引擎中的鼓手 第2集 鼓组shape key", "Drummer In Unreal Engine Part 2: Make Shape Key"),
        url: "https://www.bilibili.com/video/BV1Fe5A6mEQA/",
        urlEn: "https://www.youtube.com/watch?v=xW8A3cRViYA",
        tags: [l("BeatBloom", "BeatBloom")],
      },
      {
        title: l("虚幻引擎中的鼓手 第3集 导出人物和鼓组", "Drummer In Unreal Engine Part 3: Export Model"),
        url: "https://www.bilibili.com/video/BV1ys526kEXw/",
        urlEn: "https://www.youtube.com/watch?v=a4ma8jqq8e0",
        tags: [l("BeatBloom", "BeatBloom")],
      },
      {
        title: l("虚幻引擎中的鼓手 第4集 设置人物和鼓组的Control Rig", "Drummer In Unreal Engine Part 4: Setting Control Rig"),
        url: "https://www.bilibili.com/video/BV1hz5B6cEPP/",
        urlEn: "https://www.youtube.com/watch?v=b6nGcIHoAXM",
        tags: [l("BeatBloom", "BeatBloom")],
      },
      {
        title: l("虚幻引擎中的鼓手 第5集 设置鼓手基准姿势", "Drummer In Unreal Engine Part 5: Setting Reference Poses"),
        url: "https://www.bilibili.com/video/BV1VhLH6xEEp/",
        urlEn: "https://www.youtube.com/watch?v=XaRvL67xtwg",
        tags: [l("BeatBloom", "BeatBloom")],
      },
      {
        title: l("虚幻引擎中的鼓手 第6集 从原音到动画全流程实录", "Drummer In Unreal Engine Part 6: Generate Animation"),
        url: "https://www.bilibili.com/video/BV1PxLr6hEVV/",
        urlEn: "https://www.youtube.com/watch?v=XLV5YC4VTLE",
        tags: [l("BeatBloom", "BeatBloom")],
      },
      {
        title: l("虚幻引擎中的鼓手 第7集 其它细节", "Drummer In Unreal Engine Part 7: Other Details"),
        url: "https://www.bilibili.com/video/BV1paLa6vEHh/",
        urlEn: "https://www.youtube.com/watch?v=DU9iEFHb7Gs",
        tags: [l("BeatBloom", "BeatBloom")],
      },
      {
        title: l("虚幻引擎中的鼓手 第8集 编辑修改动作", "Drummer In Unreal Engine Part 8: Modify Animation"),
        url: "https://www.bilibili.com/video/BV171Le6eEDC/",
        urlEn: "https://www.youtube.com/watch?v=ChwiPhBh0eQ",
        tags: [l("BeatBloom", "BeatBloom")],
      },
      {
        title: l("3.beatBloom鼓组设置", "3.beatBloom鼓组设置"),
        url: "https://www.bilibili.com/video/BV1PWSqBKEfe/",
        tags: [l("BeatBloom", "BeatBloom")],
      },
      {
        title: l("4.beatBloom人物控件的绑定", "4.beatBloom人物控件的绑定"),
        url: "https://www.bilibili.com/video/BV1j6SqBsEKM/",
        tags: [l("BeatBloom", "BeatBloom")],
      },
      {
        title: l("5.beatBloom自定义鼓组及动作", "5.beatBloom自定义鼓组及动作"),
        url: "https://www.bilibili.com/video/BV1oBSqBdEfv/",
        tags: [l("BeatBloom", "BeatBloom")],
      },
    ],
  },
  {
    id: "series-fret-dance",
    title: l("FretDance 吉他手系列", "FretDance — guitarist series"),
    desc: l("Unreal 侧的完整教程，以及 Blender 侧从开篇到「让人物演奏乐曲」的操作细节。", "The full Unreal series plus the Blender-side walkthrough from setup to making the performer play."),
    items: [
      {
        title: l("虚幻引擎中的吉他手 - FretDanceUnreal系列教程", "虚幻引擎中的吉他手 - FretDanceUnreal系列教程 第0集 简介"),
        url: "https://www.bilibili.com/video/BV1NZodB3Ewf/",
        urlEn: "https://www.youtube.com/watch?v=hPvs62F2rU0",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("虚幻引擎中的吉他手 1. 人物的导出", "虚幻引擎中的吉他手 1.人物的导出"),
        url: "https://www.bilibili.com/video/BV1KCo6BvEUD/",
        urlEn: "https://www.youtube.com/watch?v=-smm2-GBexo",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("虚幻引擎中的吉他手 第2集 吉它的导出", "虚幻引擎中的吉他手 第2集 吉他的导出"),
        url: "https://www.bilibili.com/video/BV1sc96BpEyY/",
        urlEn: "https://www.youtube.com/watch?v=5LXXVz5NAzo",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("虚幻引擎中的吉他手 第3集 人物和吉他的导入", "虚幻引擎中的吉他手 第3集 人物和吉他的导入"),
        url: "https://www.bilibili.com/video/BV19j9mBsEBF/",
        urlEn: "https://www.youtube.com/watch?v=RzbwNseZ8CA",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("虚幻引擎中的吉他手 第4集 人物基准状态的设置", "虚幻引擎中的吉他手 第4集 人物基准状态的设置"),
        url: "https://www.bilibili.com/video/BV1JS94BGEzS/",
        urlEn: "https://www.youtube.com/watch?v=rkRtMdBJBlM",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("虚幻引擎中的吉他手 第5集 生成动画以及debug", "虚幻引擎中的吉他手 第5集 生成动画以及debug"),
        url: "https://www.bilibili.com/video/BV1sZ9qB2EMD/",
        urlEn: "https://www.youtube.com/watch?v=-HCCcGp-ktw",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("虚幻引擎中的吉他手 第6集 电吉他右手设置", "虚幻引擎中的吉他手 第6集  电吉他右手设置"),
        url: "https://www.bilibili.com/video/BV13A9qBsEbh/",
        urlEn: "https://www.youtube.com/watch?v=Av-D135fnSM",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("虚幻引擎中的吉他手 第7集 动作retarget及混合", "虚幻引擎中的吉他手 第7集 动作retarget以及混合"),
        url: "https://www.bilibili.com/video/BV1v8RTB7E3J/",
        urlEn: "https://www.youtube.com/watch?v=sFiaNCiNLuQ",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("虚幻引擎中的吉他手 第8集 用kawaii插件添加摆动效果", "虚幻引擎中的吉他手 第8集  用kawaii插件添加头发动作"),
        url: "https://www.bilibili.com/video/BV1qiRVBqEYS/",
        urlEn: "https://www.youtube.com/watch?v=B9tShVR95iU",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("虚幻引擎中的吉他手 第9集 写在最后", "虚幻引擎中的吉他手 第9集 写在最后"),
        url: "https://www.bilibili.com/video/BV1sKRjBREvD/",
        urlEn: "https://www.youtube.com/watch?v=C5xZde_Vwvs",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("开篇", "开篇"),
        url: "https://www.bilibili.com/video/BV1jYp8zQEh2/",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("安装插件，导入模型，初始化模型", "安装插件，导入模型，初始化模型"),
        url: "https://www.bilibili.com/video/BV1jYp8zXEmE/",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("吉他的导入和摆放", "吉他的导入和摆放"),
        url: "https://www.bilibili.com/video/BV1jYp8zQEyR/",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("左手基准状态设置", "左手基准状态设置"),
        url: "https://www.bilibili.com/video/BV1jYp8zQEjL/",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("右手基准状态的设置", "右手基准状态的设置"),
        url: "https://www.bilibili.com/video/BV1iYp8zQEd5/",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("设置吉他四个基准位置以及控件材质", "设置吉他四个基准位置以及控件材质"),
        url: "https://www.bilibili.com/video/BV1jYp8zQE8K/",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("导出人物设置数据", "导出人物设置数据"),
        url: "https://www.bilibili.com/video/BV1jYp8zQE3p/",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("让人物演奏乐曲", "让人物演奏乐曲"),
        url: "https://www.bilibili.com/video/BV17Yp8zQETs/",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("给吉它弦添加shape key", "给吉它弦添加shape key"),
        url: "https://www.bilibili.com/video/BV17ap8zpE5D/",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("辅助线的设置", "辅助线的设置"),
        url: "https://www.bilibili.com/video/BV1jYp8zQE8F/",
        tags: [l("FretDance", "FretDance")],
      },
      {
        title: l("使用pick的右手设置", "使用pick的右手设置"),
        url: "https://www.bilibili.com/video/BV1F7WuzKEba/",
        tags: [l("FretDance", "FretDance")],
      },
    ],
  },
  {
    id: "series-wind-rise",
    title: l("WindRise 管乐篇", "WindRise — wind instruments"),
    desc: l("长笛 Shape Key、人物 Shape Key 与基准姿势，到 Unreal 里实测。", "Flute shape keys, performer shape keys and baseline poses, then a real Unreal test."),
    items: [
      {
        title: l("WindRise篇：制作长笛 shape key", "Creating Shape Keys for Wind Instruments"),
        url: "https://www.bilibili.com/video/BV1xKbK6eEHc/",
        urlEn: "https://www.youtube.com/watch?v=TLh51lqOhZQ",
        tags: [l("WindRise", "WindRise")],
      },
      {
        title: l("WindRise篇：人物shape key和基准姿势", "Flute Performance: Breath States & Finger Postures"),
        url: "https://www.bilibili.com/video/BV1vyY868E7r/",
        urlEn: "https://www.youtube.com/watch?v=D1jdD1UpliM",
        tags: [l("WindRise", "WindRise")],
      },
      {
        title: l("WindRise篇：在Unreal里测试哥伦比娅", "MIDI to UE5: Colombina Flute Animation Test"),
        url: "https://www.bilibili.com/video/BV1isY96hE6P/",
        urlEn: "https://www.youtube.com/watch?v=8Igk1oFdO1U",
        tags: [l("WindRise", "WindRise")],
      },
    ],
  },
  {
    id: "plugin-workflow",
    title: l("插件与工作流", "Plugins and workflow"),
    desc: l("Maya / Blender / Unreal 的接入、骨骼改造与各类功能更新。", "Maya / Blender / Unreal integration, rig rework and feature updates."),
    items: [
      {
        title: l("音乐人偶插件跑通Maya", "音乐人偶插件跑通Maya"),
        url: "https://www.bilibili.com/video/BV1Miq5B1ETx/",
        note: l("插件接入 Maya 的演示。", "Getting the plugin working inside Maya."),
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("在Maya中用Midi生成动画！小提琴项目插件跑通，阶段总结。", "在Maya中用Midi生成动画！小提琴项目插件跑通，阶段总结。"),
        url: "https://www.bilibili.com/video/BV1dLvaBNEAb/",
        note: l("Maya 里用 MIDI 生成小提琴动画。", "Generating violin animation from MIDI in Maya."),
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("在Blender中改造音乐人偶骨骼", "在Blender中改造音乐人偶骨骼"),
        url: "https://www.bilibili.com/video/BV1qSq9BuE8Y/",
        note: l("在 Blender 中改造音乐人偶骨骼。", "Reworking the MusicDoll rig in Blender."),
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("在Maya中改造音乐人偶骨骼：操作细节讲解", "在Maya中改造音乐人偶骨骼：操作细节讲解"),
        url: "https://www.bilibili.com/video/BV1hYBFB5EMc/",
        note: l("在 Maya 中改造骨骼的操作细节。", "Rig rework details in Maya."),
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("在Maya和Blender里一键修复手指骨骼", "在Maya和Blender里一键修复手指骨骼"),
        url: "https://www.bilibili.com/video/BV1CTmxB8Eye/",
        note: l("在 Maya 和 Blender 里一键修手指骨骼。", "One-click finger bone fix in Maya and Blender."),
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("2.常见骨骼问题的修复", "2.常见骨骼问题的修复"),
        url: "https://www.bilibili.com/video/BV1wtSqBYExK/",
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("1.模型初始化以及控件介绍视频", "1.模型初始化以及控件介绍视频"),
        url: "https://www.bilibili.com/video/BV1ptSqBYE9j/",
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("成功移植Blender风格IK节点到Unreal", "Porting Blender IK to Unreal Engine: Smooth Fingers"),
        url: "https://www.bilibili.com/video/BV1Ljuy6kEtC/",
        urlEn: "https://www.youtube.com/watch?v=41YIqZAS5ww",
        note: l("把 Blender 风格的 IK 节点移植到 Unreal。", "Porting Blender-style IK nodes to Unreal."),
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("新增：电吉他摇把动作", "新增：电吉他摇把动作"),
        url: "https://www.bilibili.com/video/BV1STjF6xEUe/",
        note: l("新增电吉他摇把动作。", "New whammy-bar motion for electric guitar."),
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("FretDance新增基于音乐的吉他偏移动画", "FretDance新增基于音乐的吉他偏移动画"),
        url: "https://www.bilibili.com/video/BV1c7Lc6yEgS/",
        note: l("FretDance 新增基于音乐的吉他偏移动画。", "FretDance adds music-driven guitar offset animation."),
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("虚拟乐手们现在还能唱歌了！", "虚拟乐手们现在还能唱歌了！"),
        url: "https://www.bilibili.com/video/BV1fENc62E8h/",
        note: l("虚拟乐手开始支持演唱口型。", "Virtual performers now support singing."),
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("将温迪改造成吉他手 - MusicDoll Blender插件应用实录", "Introduction to MusicDoll for Blender"),
        url: "https://www.bilibili.com/video/BV14duR6dEjC/",
        urlEn: "https://www.youtube.com/watch?v=QvbI5tANUSQ",
        note: l("把角色改造成吉他手的完整实录。", "Full record of turning a character into a guitarist."),
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("把温迪弄到虚幻引擎里去 - FretDance从Blender到Unreal一小时实录", "Blender to Unreal: Automated MIDI Guitar Performance Guide"),
        url: "https://www.bilibili.com/video/BV1G3uB6QED3/",
        urlEn: "https://www.youtube.com/watch?v=aFS1-BEyUBs",
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("新写的Midi音符修剪功能，可在seemusic出画面前使用", "新写的Midi音符修剪功能，可在seemusic出画面前使用"),
        url: "https://www.bilibili.com/video/BV1VBZTBXE2A/",
        note: l("MIDI 音符修剪功能（可在出画面前使用）。", "MIDI note trimming, useful before rendering."),
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("如何通过乐器组合来搜索和过滤midi文件？", "如何通过乐器组合来搜索和过滤midi文件？"),
        url: "https://www.bilibili.com/video/BV1NCtr6AEUy/",
        note: l("用乐器组合搜索与过滤 MIDI 文件。", "Searching and filtering MIDI files by instrument combination."),
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("Unreal与Blender实时渲染钢琴演奏动画对比", "Realtime Rendering In Unreal And Blender, KeyRipple test with Midi Music \"Heroism\" Play by Citlali"),
        url: "https://www.bilibili.com/video/BV1SizbBZEKD/",
        urlEn: "https://www.youtube.com/watch?v=Ow-IktOCWLI",
        note: l("Unreal 与 Blender 实时渲染效果对比。", "Unreal vs Blender real-time rendering comparison."),
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("StringFlowUnreal跑通", "StringFlowUnreal跑通"),
        url: "https://www.bilibili.com/video/BV16gZyBqEZ4/",
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("在Unreal里演奏吉它 - FretDanceUnreal跑通", "在Unreal里演奏吉它 - FretDanceUnreal跑通"),
        url: "https://www.bilibili.com/video/BV1vTPXzAESR/",
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("在Unreal中玩架子鼓-BeatBloomUnreal跑通", "在Unreal中玩架子鼓-BeatBloomUnreal跑通"),
        url: "https://www.bilibili.com/video/BV1aJwgzTEsD/",
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("ZhengDrift跑通Unreal", "ZhengDrift跑通Unreal"),
        url: "https://www.bilibili.com/video/BV1xMDxB6Epf/",
        urlEn: "https://www.youtube.com/watch?v=Fv6aN7O_-fg",
        tags: [l("工作流", "Workflow")],
      },
      {
        title: l("课间音乐欣赏：双吉他 风向仪 DEPAPEPE", "风向仪 双吉他"),
        url: "https://www.bilibili.com/video/BV18x9mBJEjk/",
        urlEn: "https://www.youtube.com/watch?v=h-zsCyMpO8Y",
        tags: [l("工作流", "Workflow")],
      },
    ],
  },
  {
    id: "final-results",
    title: l("成品效果", "Finished results"),
    desc: l("七款乐器各自的代表作（更多演奏视频见作者 B 站空间）。", "A representative pick per instrument — more performances are on the YouTube channel."),
    items: [
      {
        title: l("超华丽编曲 魔女级演奏难度 Unravel 钢琴 博丽灵梦演奏", "超华丽编曲 魔女级演奏难度 Unravel 钢琴 博丽灵梦演奏"),
        url: "https://www.bilibili.com/video/BV11fEx6YEmp/",
        note: l("钢琴：Unravel。", "Piano: Unravel."),
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("保卫黄河钢琴协奏曲 - 博丽灵梦", "Defend the Yellow River Piano Concerto"),
        url: "https://www.bilibili.com/video/BV1T61oBHErj/",
        urlEn: "https://www.youtube.com/watch?v=VTNEdUHjo4E",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("双钢琴 拉三第三乐章 茜特拉莉 | 博丽灵梦演奏", "Two Pianos - Rachmaninoff Piano Concerto No. 3, Mvt. 3 | Performed by Citlali & Reimu Hakurei"),
        url: "https://www.bilibili.com/video/BV1emNR64EPr/",
        urlEn: "https://www.youtube.com/watch?v=2FnJwfhO6io",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("博丽灵梦 -《梁祝》钢琴版", "Reimu Hakurei Plays \"The Butterfly Lovers\" (梁祝)  on Piano"),
        url: "https://www.bilibili.com/video/BV1Rr1gBPE45/",
        urlEn: "https://www.youtube.com/watch?v=op78SOlo3fw",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("[原神弦乐团] 勃兰登堡协奏曲第一乐章 midi音源版", "[原神弦乐团] 勃兰登堡协奏曲第一乐章 midi音源版"),
        url: "https://www.bilibili.com/video/BV1BMt563EV8/",
        note: l("弦乐：勃兰登堡协奏曲第一乐章。", "Strings: Brandenburg Concerto No.1, first movement."),
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("阿蕾奇诺 优菈 琴 菲谢尔 | 如歌的行板 - 弦乐四重奏", "Tchaikovsky - Andante Cantabile (String Quartet No. 1) | Animusic Performance"),
        url: "https://www.bilibili.com/video/BV1Bs8q61E1L/",
        urlEn: "https://www.youtube.com/watch?v=_rQg1BGbLPY",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("Piano Trio in A minor Op. 50 violin cello 茜特拉莉 | 阿蕾奇诺 | 菲谢尔", "Tchaikovsky - Piano Trio in A minor Op. 50 violin cello"),
        url: "https://www.bilibili.com/video/BV1wBPFzwEDn/",
        urlEn: "https://www.youtube.com/watch?v=5c9rES264ZI",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("基尼奇- God knows", "基尼奇- God knows"),
        url: "https://www.bilibili.com/video/BV1huaWzjEKV/",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("神里绫华指弹 - Lewis and Clark （Cover)", "神里绫华指弹 - Lewis and Clark （Cover)"),
        url: "https://www.bilibili.com/video/BV1hKxtzVE3b/",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("沙漠的流浪者，亦有她的庄严｜婕德 - 古典吉他独奏 巴赫BWV1004 恰空", "沙漠的流浪者，亦有她的庄严｜婕德 - 古典吉他独奏 巴赫BWV1004 恰空"),
        url: "https://www.bilibili.com/video/BV1XAW4zBE8c/",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("原神5人乐队 - Every Time I Die", "原神5人乐队 - Every Time I Die"),
        url: "https://www.bilibili.com/video/BV1ofHfzYErc/",
        note: l("鼓：五人乐队 Every Time I Die。", "Drums: five-piece band, Every Time I Die."),
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("鼓手辛焱：Every Time I Die", "Xinyan - Every Time I Die (Drum Cover)"),
        url: "https://www.bilibili.com/video/BV1RGpBzMEmg/",
        urlEn: "https://www.youtube.com/watch?v=dFoQSiLmLu0",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("BanG Dream!鼓手演绎EVA神曲", "Eva Theme But It's Drummer From BanG Dream! Going Wild"),
        url: "https://www.bilibili.com/video/BV1DW4Hz8EYQ/",
        urlEn: "https://www.youtube.com/watch?v=oriYR1phWxM",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("钟离 | 古筝 | 牵丝戏", "钟离 古筝 牵丝戏 Chinese Zither"),
        url: "https://www.bilibili.com/video/BV1HB9pBPEMf/",
        urlEn: "https://www.youtube.com/watch?v=aw-_uBqD42Q",
        note: l("古筝：牵丝戏。", "Guzheng: Qian Si Xi."),
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("渔舟唱晚 翻弹 by 丝柯克", "Fishermen’s Evening Song Covered by Skirk"),
        url: "https://www.bilibili.com/video/BV1jsAwzRExS/",
        urlEn: "https://www.youtube.com/watch?v=QDDi_1nHbmE",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("春江花月夜", "春江花月夜"),
        url: "https://www.bilibili.com/video/BV13Z7v6AEeN/",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("如果你没见过竖琴弹唱 那今天你见到了 | 芭芭拉 Star Of County Down", "Star of County Down Barbara Harp"),
        url: "https://www.bilibili.com/video/BV12fNu6HEfc/",
        urlEn: "https://www.youtube.com/watch?v=sVG2_iIJlJw",
        note: l("竖琴：Star Of County Down。", "Harp: Star Of County Down."),
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("芭芭拉 竖琴演奏 <深海少女>", "芭芭拉 竖琴演奏 <深海少女>"),
        url: "https://www.bilibili.com/video/BV1PGjT6KEHw/",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("枫叶城 | 笛子 钢琴版", "City of Maple Leaves (Flute & Piano Cover)"),
        url: "https://www.bilibili.com/video/BV16Qg86LE6L/",
        urlEn: "https://www.youtube.com/watch?v=A5AvN_InziM",
        note: l("管乐：枫叶城（笛子 + 钢琴）。", "Wind: Maple Leaf City (flute + piano)."),
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("牧童短笛二重奏 | 七七得到了史诗性加强，但是在你没想到的方向上", "牧童短笛二重奏 | 七七得到了史诗性加强，但是在你没想到的方向上"),
        url: "https://www.bilibili.com/video/BV1qPKk6wEUW/",
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("当东方幻想乡闯入蒙德城！四人小乐队合奏<妖怪之山>", "当东方幻想乡闯入蒙德城！四人小乐队合奏<妖怪之山>"),
        url: "https://www.bilibili.com/video/BV13mnbziEwE/",
        note: l("合奏：四人小乐队《妖怪之山》。", "Ensemble: four-piece band, Youkai Mountain."),
        tags: [l("成品", "Showcase")],
      },
      {
        title: l("原神四人组演奏unravel", "原神四人组演奏unravel"),
        url: "https://www.bilibili.com/video/BV1W1aizgEYe/",
        tags: [l("成品", "Showcase")],
      },
    ],
  },
];

/** 页面顶部展示的收录条目总数（由数据自动算出） */
export const SHOWCASE_ITEM_COUNT: number = SHOWCASE_GROUPS.reduce(
  (n, g) => n + g.items.length,
  0,
);
